/**
 * Styleguide Tools
 */

import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import z from "zod";

import { ApiError } from "../api/errors.js";
import {
  getStyleguideTree,
  listStyleguides,
  listStyleguideVersions,
} from "../api/styleguide.js";
import {
  formatStyleguideListAsJson,
  formatTreeAsJson,
} from "../common/formatters/styleguide.js";
import { getCredentials } from "../common/credentials.js";
import { registerTrackedToolCall } from "../logging.js";
import { ServerContext } from "../types/server.js";
import { captureException } from "../common/errors.js";

export function registerListStyleguidesTool(server: McpServer) {
  registerTrackedToolCall(
    server,
    "list-styleguides",
    {
      title: "List styleguides",
      description:
        "Call this FIRST whenever the user asks to build, generate, modify, or review UI — components, pages, layouts, themes, or styling — in a project that may have a zeroheight design system. Discovers available design systems that provide authoritative guidance for components, patterns, tokens, and styles. If multiple systems are available, confirm with the user which to use before continuing. Do not begin generating UI code until this has been called and the relevant styleguide has been consulted.",
      inputSchema: {},
      outputSchema: {
        styleguides: z
          .array(
            z.object({
              id: z
                .number()
                .describe("The unique identifier for the styleguide."),
              name: z.string().describe("The name of the styleguide."),
            }),
          )
          .describe("List of available design system styleguides."),
      },
      annotations: {
        title: "List styleguides",
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: false,
      },
    },
    async (_, { authInfo }, clientUserAgent) => {
      const credentials = await getCredentials(authInfo);
      const styleguides = await listStyleguides(credentials, clientUserAgent);

      if (styleguides.length === 0) {
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: "There are no accessible styleguides. Check your auth credentials and make sure you have at least one styleguide in zeroheight.",
            },
          ],
        };
      }

      const structuredContent = formatStyleguideListAsJson(styleguides);

      return {
        content: [
          {
            type: "text",
            text: JSON.stringify(structuredContent, null, 2),
          },
        ],
        structuredContent,
      };
    },
  );
}

const pageNodeSchema: z.ZodType = z.lazy(() =>
  z.union([
    z.object({
      id: z
        .number()
        .describe("The page ID. Use with `get-page` to fetch its content."),
      url: z.string().describe("The URL of the page."),
    }),
    z.record(z.string(), pageNodeSchema),
  ]),
);

const releasesSchema = z
  .array(
    z.object({
      id: z
        .number()
        .describe(
          "The unique identifier for the release. Pass as `releaseId` to other tools when the user requests a specific release.",
        ),
      name: z.string().describe("The name of the release."),
      default: z
        .boolean()
        .describe("Whether this is the default release shown to viewers."),
      working_version: z
        .boolean()
        .describe("Whether this is the current work-in-progress release."),
      created_at: z.string().describe("When the release was created."),
      release_notes: z
        .string()
        .nullable()
        .describe("Release notes for this release."),
    }),
  )
  .describe("Available releases for the styleguide, newest first.");

export function registerListReleasesTool(
  server: McpServer,
  context: ServerContext,
) {
  const singleStyleguideMode =
    context.connectionMode === "remote_single_styleguide";

  registerTrackedToolCall(
    server,
    "list-releases",
    {
      title: "List releases in a styleguide",
      description:
        "List available releases for a styleguide. Only call this when the user has explicitly asked about releases or wants content from a specific release. Do not use `releaseId` in other tools unless the user has requested a particular release — all tools default to the latest published content.",
      inputSchema: {
        ...(singleStyleguideMode
          ? {}
          : {
              styleguideId: z
                .number()
                .int()
                .min(1)
                .describe(
                  "The design system to list releases for. Use `list-styleguides` to discover available systems.",
                ),
            }),
      },
      outputSchema: {
        releases: releasesSchema,
      },
      annotations: {
        readOnlyHint: true,
      },
    },
    async (args, { authInfo }, clientUserAgent) => {
      try {
        const credentials = await getCredentials(authInfo);
        const styleguideId = singleStyleguideMode
          ? context.styleguideId
          : (args as { styleguideId: number }).styleguideId;

        if (typeof styleguideId !== "number") {
          throw new Error("Missing styleguideId");
        }

        const versions = await listStyleguideVersions(
          credentials,
          styleguideId,
          clientUserAgent,
        );
        const structuredContent = { releases: releasesSchema.parse(versions) };

        return {
          content: [
            {
              type: "text",
              text: JSON.stringify(structuredContent, null, 2),
            },
          ],
          structuredContent,
        };
      } catch (e) {
        return {
          isError: true,
          content: [
            {
              type: "text",
              text:
                e instanceof ApiError
                  ? e.message
                  : "Could not get releases for this styleguide.",
            },
          ],
        };
      }
    },
  );
}

export function registerListPagesTool(
  server: McpServer,
  context: ServerContext,
) {
  const singleStyleguideMode =
    context.connectionMode === "remote_single_styleguide";

  registerTrackedToolCall(
    server,
    "list-pages",
    {
      title: "List pages in a styleguide",
      description: singleStyleguideMode
        ? "Call this to locate the documentation page for a component, pattern, or guideline in the current design system BEFORE generating UI that uses it. Returns the full navigation hierarchy — categories, pages, and tabs — so you can identify which page to fetch with `get-page`. Do not invent component or pattern names; only reference pages that appear in this tree."
        : "Call this immediately after `list-styleguides` to locate the documentation page for a component, pattern, or guideline BEFORE generating UI that uses it. Returns the full navigation hierarchy — categories, pages, and tabs — so you can identify which page to fetch with `get-page`. Do not invent component or pattern names; only reference pages that appear in this tree.",
      inputSchema: {
        ...(singleStyleguideMode
          ? {}
          : {
              styleguideId: z
                .number()
                .int()
                .min(1)
                .describe(
                  "The design system to reference. Use `list-styleguides` to discover available systems.",
                ),
            }),
        releaseId: z
          .number()
          .int()
          .min(1)
          .optional()
          .describe(
            "Optional. Do not provide unless the user has explicitly asked for a specific release. Use `list-releases` to discover available releases.",
          ),
      },
      outputSchema: {
        ...(singleStyleguideMode
          ? {
              styleguideName: z
                .string()
                .describe("The name of the styleguide."),
            }
          : {}),
        pages: z
          .record(z.string(), pageNodeSchema)
          .describe(
            "A hierarchical navigation tree of the design system. Keys are category or page names. Page leaf nodes have { id, url } properties; category nodes contain further nested objects.",
          ),
      },
      annotations: {
        title: "List pages in a styleguide",
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: false,
      },
    },
    async (args, { authInfo }, clientUserAgent) => {
      const releaseId = (args as { releaseId?: number }).releaseId;
      try {
        const credentials = await getCredentials(authInfo);
        const styleguideId =
          context.connectionMode === "remote_single_styleguide"
            ? context.styleguideId
            : (args as { styleguideId: number }).styleguideId;

        if (typeof styleguideId !== "number") {
          throw new Error("Missing styleguideId");
        }

        const tree = await getStyleguideTree(
          credentials,
          styleguideId,
          clientUserAgent,
          releaseId,
        );

        if (tree.length === 0) {
          throw new Error("Empty styleguide tree");
        }

        const formattedTree = formatTreeAsJson(tree);
        const structuredContent = singleStyleguideMode
          ? { styleguideName: context.styleguideName, pages: formattedTree }
          : { pages: formattedTree };

        return {
          content: [
            {
              type: "text",
              text: JSON.stringify(structuredContent, null, 2),
            },
          ],
          structuredContent,
        };
      } catch {
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: "Could not get the navigation tree for this styleguide.",
            },
          ],
        };
      }
    },
  );
}
